/**
 * The public share link of one made asset (a kit asset version or an AI reel): the contract of
 * music24-api (`/accounts/:accountId/asset-shares`, `/public/asset-shares/:token`, the link
 * preview page `/a/:token`) and the web app (the page `/app/a/<token>`). The link is
 * `https://share.music24.com/a/<token>` (staging `share.musicpawa.com`): music24-api answers a page
 * with the link preview tags there and sends a person on to the web app page. sml-preview sends the
 * old `link.music24.com/a/<token>` links to the share host. Type imports only (from a file with no imports): every project may read this file.
 */
import type { KitFontId, ReleaseThemeId, ThemeColours, ThemeTextCase } from './releaseThemes';

/** What a share link points at: one version of a kit asset, or one AI reel. */
export type AssetShareKind = 'kitAsset' | 'reel';

export const ASSET_SHARE_KINDS: readonly AssetShareKind[] = ['kitAsset', 'reel'];

/** One live share link, as its owner sees it. */
export interface SerializedAssetShare {
  token: string;
  /**
   * The link to copy: `<SHARE_BASE_URL>/a/<token>` (`https://share.music24.com/a/<token>`). It shows
   * the link preview and then opens the web app page `<SMARTLINKS_APP_URL>/a/<token>`.
   */
  url: string;
  kind: AssetShareKind;
  /** The uuid of the kit asset version or of the reel. */
  assetUuid: string;
  createdAt: string;
}

/** GET, POST `/accounts/:accountId/asset-shares`. `share` is null when the asset has no live link. */
export interface AssetShareResponse {
  share: SerializedAssetShare | null;
}

/** GET `/public/asset-shares/:token`: what the public page shows. No ids, no account, no email. */
export interface PublicAssetShare {
  token: string;
  mediaKind: 'image' | 'video';
  /** A path relative to the API base: `public/asset-shares/<token>/media`. Add `?download=1` for a download. */
  mediaUrl: string;
  /** A path relative to the API base: `public/asset-shares/<token>/poster`, or null when the asset has no poster. */
  posterUrl: string | null;
  contentType: string;
  width: number | null;
  height: number | null;
  durationSec: number | null;
  sizeBytes: number | null;
  /**
   * True for a preview render of the Free plan or the trial: a small file (half size, a low bit
   * rate) with the watermark. The page tells the viewer that it is a preview.
   */
  preview: boolean;
  /** The English label of the asset type (fym-shared `kitFileNames.ts`): "Story (Out now)", "AI reel 30s". */
  assetLabel: string;
  /** The download name (fym-shared `kitFileNames.ts`). */
  fileName: string;
  artist: { name: string; slug: string | null; avatarUrl: string | null };
  song: { title: string; artworkUrl: string | null };
  /** The published SmartLink of the song, or null. */
  smartLinkUrl: string | null;
  /** The look of the song that the asset was made in (the share page wears it), or null when the song has none. */
  look: PublicAssetShareLook | null;
  createdAt: string;
}

/**
 * The look of a shared asset: the release theme, the colours that the theme makes from the
 * effective kit palette (fym-shared `themeColours`) and the display type of the title. Public
 * data only: the same colours and font are on the asset itself.
 */
export interface PublicAssetShareLook {
  releaseThemeId: ReleaseThemeId;
  /** The five colours that the asset was made with (the effective kit palette). Can be empty. */
  palette: string[];
  colours: ThemeColours;
  /** The display type of the theme (or of the font override). */
  display: { fontId: KitFontId; weight: number; italic: boolean; case: ThemeTextCase; tracking: number };
}

/**
 * GET `/public/asset-shares/:token/access`: where a signed-in member opens the shared asset in the
 * web app. `access` is null for every other caller (anonymous, an API token, a user of another
 * workspace) and for a link that does not work: the ids never reach a caller without access.
 */
export interface AssetShareAccessResponse {
  access: AssetShareAccess | null;
}

/** The place of a shared asset for a member of the workspace that owns it. */
export interface AssetShareAccess {
  kind: AssetShareKind;
  /** The uuid of the kit asset version or of the reel. */
  assetUuid: string;
  /** The revision of the asset (the kit asset `version`, the reel `revision`). */
  revision: number;
  /** The release (catalog song) of the asset. */
  catalogItemUuid: string;
  /** The artist account of the asset. */
  accountId: number;
  /** The workspace (licence) that owns the artist. */
  licenseId: number;
  /** False when the caller's session acts in another workspace: the web app switches first. */
  isActiveWorkspace: boolean;
}

/** The path of the share page: on the share host, under the web app (`/app`) and on the old share host. */
export const ASSET_SHARE_PATH_PREFIX = '/a/';

/** A share token: 32 random bytes in base64url make 43 characters. */
export const ASSET_SHARE_TOKEN_PATTERN = /^[A-Za-z0-9_-]{22,64}$/;

/** True when `value` has the form of a share token. */
export function isAssetShareToken(value: unknown): value is string {
  return typeof value === 'string' && ASSET_SHARE_TOKEN_PATTERN.test(value);
}

/** The path of the public page of a token: `/a/<token>` (relative to the web app base). */
export function assetSharePath(token: string): string {
  return `${ASSET_SHARE_PATH_PREFIX}${encodeURIComponent(token)}`;
}
